import React from "react";

/**
 * CommunityInsightBanner
 * Affiché dans BarcodeProductManager quand un barcode est absent de GlobalProduct
 * mais existe dans CommunityProduct (repéré par des clients).
 *
 * Props :
 *   data        — { count, product: { name, imageUrl, lastCity, lastCreatedAt } }
 *   onReference — callback déclenché quand le vendeur clique "Référencer ce produit"
 *                 reçoit { name, imageUrl } pour pré-remplir le formulaire
 */
const CommunityInsightBanner = ({ data, onReference }) => {
  if (!data?.exists) return null;

  const { count, product } = data;

  const formattedDate = product?.lastCreatedAt
    ? new Date(product.lastCreatedAt).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div
      style={{
        border: "1.5px solid #a78bfa",
        borderRadius: "10px",
        background: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
        padding: "16px",
        marginBottom: "16px",
      }}
    >
      {/* En-tête */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
        <span style={{ fontSize: "1.1rem" }}>👀</span>
        <p
          style={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#6d28d9",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            margin: 0,
          }}
        >
          Produit repéré par vos clients
        </p>
      </div>

      {/* Contenu produit */}
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", marginBottom: "12px" }}>
        {product?.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.name}
            style={{
              width: "52px",
              height: "52px",
              objectFit: "contain",
              borderRadius: "6px",
              border: "1px solid #ddd6fe",
              background: "white",
              flexShrink: 0,
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontWeight: 700,
              color: "#1e1b4b",
              fontSize: "0.95rem",
              margin: "0 0 4px",
              wordBreak: "break-word",
            }}
          >
            {product?.name}
          </p>

          {/* Méta-données */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "0.75rem",
                color: "#7c3aed",
                background: "#ede9fe",
                padding: "2px 8px",
                borderRadius: "99px",
                fontWeight: 600,
              }}
            >
              👥 {count} client{count > 1 ? "s" : ""} l'ont ajouté
            </span>

            {product?.lastCity && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "0.75rem",
                  color: "#5b21b6",
                  background: "#ede9fe",
                  padding: "2px 8px",
                  borderRadius: "99px",
                }}
              >
                📍 {product.lastCity}
              </span>
            )}

            {formattedDate && (
              <span
                style={{
                  fontSize: "0.72rem",
                  color: "#7c3aed",
                }}
              >
                Dernière mention : {formattedDate}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Message d'insight */}
      <p
        style={{
          fontSize: "0.82rem",
          color: "#5b21b6",
          margin: "0 0 12px",
          fontStyle: "italic",
        }}
      >
        Vos clients s'intéressent déjà à ce produit — référencez-le pour apparaître dans leurs résultats.
      </p>

      {/* Bouton CTA */}
      <button
        type="button"
        onClick={() => onReference({ name: product?.name || "", imageUrl: product?.imageUrl || "" })}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "9px 16px",
          background: "#7c3aed",
          color: "white",
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontWeight: 700,
          fontSize: "0.875rem",
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#6d28d9")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "#7c3aed")}
      >
        ➕ Référencer ce produit
      </button>
    </div>
  );
};

export default CommunityInsightBanner;
